import { useCallback, useMemo, useState } from "react";
import { FORM_ERROR_KEY, OTHER_SENTINEL } from "./kinds";
import { addressMissing } from "./model/address";

/**
 * The client half of the form rules (spec-widget-mcp 05 Step 3; §7 "Rule: the stepper").
 * THIS FILE IS THE ONLY HOME of "hidden fields are never required and never submitted" on the
 * client (§7 rule 3). Server validation against `submitSchema` is the authority (D-W23); this is
 * immediate feedback only.
 *
 * State: `{ values: Record<string, string|string[]|object>, touched: Set<string>, group: number }`,
 * never written to storage (§7 rule 6). Other's typed text lives under `otherKey(name)`.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Where a choice's Other text is held in `values` (never a submitted key). */
export const otherKey = (name) => `${name}${OTHER_SENTINEL}`;

/** Every field of a data_collection payload, in document order. */
export const allFields = (payload) =>
	(Array.isArray(payload?.groups) ? payload.groups : []).flatMap((g) => (Array.isArray(g?.fields) ? g.fields : []));

/** Whether a value counts as filled: a non-blank string, a non-empty array, an object with a filled part. */
export function isFilledValue(value) {
	if (value === null || value === undefined) return false;
	if (Array.isArray(value)) return value.some(isFilledValue);
	if (typeof value === "object") return Object.values(value).some(isFilledValue);
	return String(value).trim() !== "";
}

/** A choice's submitted value: Other's sentinel replaced by the typed text (D-W15), blanks dropped. */
export function resolveChoice(value, otherText = "") {
	const typed = String(otherText ?? "").trim();
	if (Array.isArray(value)) return value.map((v) => (v === OTHER_SENTINEL ? typed : v)).filter((v) => isFilledValue(v));
	return value === OTHER_SENTINEL ? typed : value;
}

const fieldValue = (field, values) =>
	(field.type === "choice" || field.type === "select") ? resolveChoice(values[field.name], values[otherKey(field.name)]) : values[field.name];

/**
 * `showWhen` (`equals` · `notEquals` · `contains` · `isFilled`). A field whose target is itself hidden
 * is hidden. `contains` is array membership — it is what reveals an input from a multi-select (D-W15).
 * @param {object} field
 * @param {object} values
 * @param {object[]} [fields] the whole form, to follow a hidden target
 */
export function isVisible(field, values, fields = [], depth = 0) {
	const cond = field?.showWhen;
	if (!cond || typeof cond.field !== "string") return true;
	const target = fields.find((f) => f.name === cond.field);
	if (target && depth < 16 && !isVisible(target, values, fields, depth + 1)) return false;
	const v = target ? fieldValue(target, values) : values[cond.field];
	if ("equals" in cond) return !Array.isArray(v) && isFilledValue(v) && String(v) === String(cond.equals);
	if ("notEquals" in cond) return Array.isArray(v) || String(v ?? "") !== String(cond.notEquals);
	if ("contains" in cond) return Array.isArray(v) ? v.includes(cond.contains) : v === cond.contains;
	if ("isFilled" in cond) return isFilledValue(v) === cond.isFilled;
	return true;
}

/**
 * One field's error, or null. Required only when visible; `pattern`, `min`/`max`, `maxLength`, email
 * shape, digits for `otp`. Returns the field's own `errorMessage` when present, else `messages.required`
 * / `messages.invalid`.
 */
export function fieldError(field, values, { fields = [], messages = {} } = {}) {
	if (!isVisible(field, values, fields)) return null;
	const own = field.errorMessage;
	const required = own || messages.required || "Required";
	const invalid = own || messages.invalid || "Invalid";
	const v = fieldValue(field, values);
	if (field.type === "address") {
		if (!isFilledValue(v)) return field.required ? required : null;
		return addressMissing(v).length > 0 ? required : null;
	}
	if (!isFilledValue(v)) return field.required ? required : null;
	if (Array.isArray(v) || typeof v === "object") return null;
	const s = String(v).trim();
	if (field.type === "number") {
		const n = Number(s);
		if (!Number.isFinite(n)) return invalid;
		if ((typeof field.min === "number" && n < field.min) || (typeof field.max === "number" && n > field.max)) return invalid;
		return null;
	}
	if (field.type === "email" && !EMAIL.test(s)) return invalid;
	if (field.type === "otp" && !/^[0-9]+$/.test(s)) return invalid;
	if (typeof field.maxLength === "number" && s.length > field.maxLength) return invalid;
	if (typeof field.pattern === "string" && field.pattern) {
		try {
			if (!new RegExp(field.pattern).test(s)) return invalid;
		} catch (_) { /* an unparseable pattern is the server's to judge */ }
	}
	return null;
}

/** What gets submitted: visible, filled fields only, typed per the submitSchema (numbers as numbers). */
export function visibleValues(payload, values) {
	const fields = allFields(payload);
	const out = {};
	for (const field of fields) {
		if (!isVisible(field, values, fields)) continue;
		const v = fieldValue(field, values);
		if (!isFilledValue(v)) continue;
		if (field.type === "number") out[field.name] = Number(String(v).trim());
		else if (field.type === "address") out[field.name] = Object.fromEntries(Object.entries(v).filter(([, p]) => isFilledValue(p)).map(([k, p]) => [k, String(p).trim()]));
		else out[field.name] = Array.isArray(v) ? v : String(v).trim();
	}
	return out;
}

const initialValue = (field) => {
	const d = field.defaultValue;
	if (d === undefined || d === null) return field.multiple ? [] : "";
	if (field.multiple) return Array.isArray(d) ? d.map(String) : [String(d)];
	return typeof d === "object" ? d : String(d);
};

/** Which group holds `name` (−1 when none). */
export const groupOf = (payload, name) =>
	(Array.isArray(payload?.groups) ? payload.groups : []).findIndex((g) => (g?.fields || []).some((f) => f.name === name));

/**
 * @param {object} payload a data_collection payload
 * @param {{ required?: string, invalid?: string }} [messages] locale copy for generic errors
 */
export function useWidgetForm(payload, messages = {}) {
	const fields = useMemo(() => allFields(payload), [payload]);
	const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f.name, initialValue(f)])));
	const [touched, setTouched] = useState(() => new Set());
	const [group, setGroup] = useState(0);
	const [serverErrors, setServerErrors] = useState({});

	const setValue = useCallback((name, value) => {
		setValues((prev) => ({ ...prev, [name]: value }));
		setServerErrors((prev) => {
			if (!(name in prev)) return prev;
			const next = { ...prev };
			delete next[name];
			return next;
		});
	}, []);
	const touch = useCallback((names) => setTouched((prev) => new Set([...prev, ...names])), []);

	const ctx = { fields, messages };
	const errorOf = (field) => serverErrors[field.name] || (touched.has(field.name) ? fieldError(field, values, ctx) : null);
	const groupFields = (i) => (payload?.groups?.[i]?.fields || []).filter((f) => isVisible(f, values, fields));
	const groupHasEmptyRequired = (i) => groupFields(i).some((f) => f.required && !isFilledValue(fieldValue(f, values)));
	const groupValid = (i) => groupFields(i).every((f) => fieldError(f, values, ctx) === null);

	/** Map a `WIDGET_SUBMIT_INVALID` verdict onto the fields; returns the group to re-enter. */
	const applyServerErrors = useCallback((errors = {}) => {
		setServerErrors(errors);
		const names = Object.keys(errors).filter((n) => n !== FORM_ERROR_KEY);
		setTouched((prev) => new Set([...prev, ...names]));
		const groups = names.map((n) => groupOf(payload, n)).filter((g) => g >= 0);
		const target = groups.length > 0 ? Math.min(...groups) : null;
		if (target !== null) setGroup(target);
		return target;
	}, [payload]);

	return {
		fields, values, touched, group, setGroup, setValue, touch, errorOf, groupFields,
		groupHasEmptyRequired, groupValid, applyServerErrors,
		formError: serverErrors[FORM_ERROR_KEY] || null,
		submission: () => visibleValues(payload, values),
	};
}
